import { useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import debtorTheme from "./debtorTheme";

const SCALE = 0.8;
const FLAG_MENU = "data-paper-width-adjusted";
const FLAG_PICKER = "data-picker-paper-adjusted";

function adjustMenuPaper(paper) {
  if (paper.hasAttribute(FLAG_MENU)) return false;
  const minWidthPx = parseFloat(paper.style.minWidth);
  if (!(minWidthPx > 0)) return false;
  const adjusted = `${minWidthPx * SCALE}px`;
  paper.style.minWidth = adjusted;
  paper.style.maxWidth = adjusted;
  for (const item of paper.querySelectorAll(".MuiMenuItem-root")) {
    item.style.fontSize = "0.8em";
  }
  paper.setAttribute(FLAG_MENU, "1");
  return true;
}

function makeAdjustPickerPaper(lastInputWidthRef) {
  return function adjustPickerPaper(paper) {
    if (paper.hasAttribute(FLAG_PICKER)) return false;
    const captured = lastInputWidthRef.current;
    if (!(captured > 0)) return false;
    const w = `${captured * SCALE}px`;
    paper.style.width = w;
    paper.style.minWidth = w;
    paper.style.fontSize = "0.1em";
    paper.setAttribute(FLAG_PICKER, "1");

    requestAnimationFrame(() => {
      const calendar = paper.querySelector(".MuiDateCalendar-root");
      if (!calendar) return;
      const calendarNatural = calendar.offsetWidth;
      const paperInner = paper.clientWidth;
      if (
        calendarNatural > 0 &&
        paperInner > 0 &&
        calendarNatural > paperInner
      ) {
        calendar.style.zoom = String(paperInner / calendarNatural);
      }
    });

    return true;
  };
}

function collectMatches(root, selector) {
  const matches = [];
  if (root.matches?.(selector)) matches.push(root);
  if (root.querySelectorAll) {
    for (const node of root.querySelectorAll(selector)) {
      matches.push(node);
    }
  }
  return matches;
}

export default function DebtorThemeProvider({ children }) {
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;

    const lastInputWidthRef = { current: 0 };

    const onFocusIn = (e) => {
      const inputBase = e.target?.closest?.(".MuiInputBase-root");
      if (inputBase && inputBase.clientWidth > 0) {
        lastInputWidthRef.current = inputBase.clientWidth;
      }
    };
    document.addEventListener("focusin", onFocusIn, true);

    const adjustPickerPaper = makeAdjustPickerPaper(lastInputWidthRef);

    const processSubtree = (root) => {
      if (root.nodeType !== 1) return false;
      let anyAdjusted = false;
      for (const paper of collectMatches(root, ".MuiMenu-paper")) {
        if (adjustMenuPaper(paper)) anyAdjusted = true;
      }
      for (const paper of collectMatches(root, ".MuiPickersPopper-paper")) {
        if (adjustPickerPaper(paper)) anyAdjusted = true;
      }
      return anyAdjusted;
    };

    const observer = new MutationObserver((mutations) => {
      let anyAdjusted = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (processSubtree(node)) anyAdjusted = true;
        }
      }
      if (anyAdjusted) {
        window.dispatchEvent(new Event("resize"));
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      observer.disconnect();
    };
  }, []);

  return <ThemeProvider theme={debtorTheme}>{children}</ThemeProvider>;
}
